import {
  getCompanions,
  inviteCompanion,
  removeCompanion,
  TravelCompanion,
} from "@/services/api/travels";
import { useI18nStore } from "@/stores/i18nStore";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import { Mail, MailCheck, MailX, Trash2, UserPlus } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";

interface CompanionManagerProps {
  travelId: string;
}

export const CompanionManager: React.FC<CompanionManagerProps> = ({
  travelId,
}) => {
  const { t } = useI18nStore();
  const fmt = useLocaleFormat();
  const [companions, setCompanions] = useState<TravelCompanion[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getCompanions(travelId);
      setCompanions(data);
    } catch {
      // companions feature may be unavailable
    } finally {
      setLoading(false);
    }
  }, [travelId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      const companion = await inviteCompanion(travelId, email.trim());
      setCompanions((prev) => [...prev, companion]);
      setEmail("");
      toast.success(t.companionInviteSent);
    } catch {
      toast.error(t.companionInviteFailed);
    } finally {
      setSending(false);
    }
  };

  const handleRemove = async (companionId: string) => {
    try {
      await removeCompanion(travelId, companionId);
      setCompanions((prev) => prev.filter((c) => c._id !== companionId));
      toast.success(t.companionRemoved);
    } catch {
      toast.error(t.error);
    }
  };

  const statusIcon = (status: TravelCompanion["status"]) => {
    if (status === "accepted")
      return <MailCheck className="h-4 w-4 text-green-500" />;
    if (status === "declined")
      return <MailX className="h-4 w-4 text-red-400" />;
    return <Mail className="h-4 w-4 text-amber-400" />;
  };

  const statusLabel = (status: TravelCompanion["status"]) => {
    if (status === "accepted") return t.companionStatusAccepted;
    if (status === "declined") return t.companionStatusDeclined;
    return t.companionStatusPending;
  };

  const statusBadgeCls = (status: TravelCompanion["status"]) => {
    const base = "rounded-full px-2 py-0.5 text-xs font-medium";
    if (status === "accepted") return `${base} bg-green-100 text-green-700`;
    if (status === "declined") return `${base} bg-red-100 text-red-600`;
    return `${base} bg-amber-100 text-amber-700`;
  };

  return (
    <div className="space-y-4">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700">
        <UserPlus className="h-4 w-4" />
        {t.travelCompanions}
      </h3>

      {/* Invite Form */}
      <form onSubmit={handleInvite} className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t.companionEmailPlaceholder}
          className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        <button
          type="submit"
          disabled={sending || !email.trim()}
          className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          <UserPlus className="h-4 w-4" />
          {sending ? "..." : t.sendInvitation}
        </button>
      </form>

      {/* Companion List */}
      {loading ? (
        <p className="text-xs text-gray-400">{t.loading}</p>
      ) : companions.length === 0 ? (
        <p className="rounded-lg bg-gray-50 px-4 py-3 text-sm text-gray-500">
          {t.noCompanionsYet}
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
          {companions.map((c) => (
            <li
              key={c._id}
              className="flex items-center justify-between px-4 py-3"
            >
              <div className="flex items-center gap-3">
                {statusIcon(c.status)}
                <div>
                  <p className="text-sm font-medium text-gray-800">
                    {c.invitedEmail}
                  </p>
                  <p className="text-xs text-gray-400">
                    {fmt.date(c.invitedAt)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={statusBadgeCls(c.status)}>
                  {statusLabel(c.status)}
                </span>
                <button
                  onClick={() => handleRemove(c._id)}
                  className="rounded p-1 text-gray-300 hover:text-red-500 transition-colors"
                  title={t.removeCompanion}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
